import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { apiRequest, queryClient } from '../lib/queryClient';

type Preview = {
  email: string;
  full_name: string;
  company_name: string;
  role: string;
};

export default function JoinPage() {
  const [, setLocation] = useLocation();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This join link is missing its token.');
      return;
    }
    apiRequest('GET', `/api/join-preview?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data: Preview) => setPreview(data))
      .catch((err: Error) => setError(err.message || 'This join link is invalid or has expired'));
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await apiRequest('POST', '/api/join', { token, password });
      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      queryClient.setQueryData(['/api/user'], data.user);
      setLocation('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join this company');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-green-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-green-100 overflow-hidden">
        <div className="bg-[#002d04] px-8 pt-8 pb-6 text-center">
          <img src="/assets/logo_eco.png" alt="Eco Ledger" className="mx-auto h-28 w-auto object-contain" />
          <p className="mt-2 text-sm text-green-100">Join your organization</p>
        </div>
        <div className="px-8 py-7">
          <h1 className="text-2xl font-semibold text-gray-900">Create your password</h1>
          {preview && (
            <p className="mt-1 text-sm text-gray-600">
              {preview.full_name ? `${preview.full_name}, you are` : 'You are'} joining{' '}
              <span className="font-medium">{preview.company_name}</span> as{' '}
              {preview.role === 'COMPANY_ADMIN' ? 'a company admin' : 'a company member'}.
              Sign in later with {preview.email}.
            </p>
          )}

          {!preview && !error && <p className="mt-4 text-sm text-gray-600">Checking your link…</p>}

          {error && (
            <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {preview && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input label="Email" value={preview.email} readOnly />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={8}
                required
              />
              <Input
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
                required
              />
              <Button type="submit" className="w-full h-11" disabled={submitting}>
                {submitting ? 'Joining…' : 'Join company'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
